// Shared base usage-bar primitives — extracted from BillingPage.tsx so the
// Stripe billing page (BillingPage.tsx) and the Shopify billing view
// (ShopifyBillingView.tsx) render the SAME bars without copy-paste drift.
//
// These are the BASE-LIMIT (no bundle credits) pieces only:
//   - usagePercent / barColor — pure math + color helpers
//   - UsageBar — a single base-allowance bar (used vs limit)
//   - OverrideDisclosure — "Limit set by Prodagi team" footnote
//   - overrideKeyFor / OVERRIDE_KEY_REMAP — FE→backend canonical key remap
//
// Bundle-credit / auto-overage rendering lives in UsageBarWithCredits and is
// intentionally NOT here — the Shopify (Managed Pricing) view uses base bars
// only, and BillingPage keeps using UsageBarWithCredits for the Stripe path.

import React from 'react';
import { AlertTriangle, Info, Wallet } from 'lucide-react';
import type { UsageSnapshot } from '../../types/billing';
import {
  TEXT_POOL_WARN_PCT,
  TEXT_POOL_CRITICAL_PCT,
  TEXT_POOL_EXHAUSTED_PCT,
} from '../../types/billing';

export const usagePercent = (used: number, limit: number): number => {
  if (limit < 0) return 0;
  if (limit === 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.round((used / limit) * 100));
};

export const barColor = (pct: number): string => {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-emerald-500';
};

// ---------- override disclosure ----------
// Plan-EffectiveLimitsAndMaintenanceBanners.md §2a (2026-05-15) — rendered
// below any usage bar whose canonical resource key appears in
// `billing.overridden_keys`. Purely informational ("the limit you see is set
// by us, not the plan default"), styled small + italic + muted. Empty
// `overridden_keys` → returns null.
export const OverrideDisclosure: React.FC<{
  resourceKey: string;
  overriddenKeys?: string[];
}> = ({ resourceKey, overriddenKeys }) => {
  if (!overriddenKeys || overriddenKeys.length === 0) return null;
  if (!overriddenKeys.includes(resourceKey)) return null;
  return (
    <p className="text-[11px] italic text-gray-500 dark:text-gray-400 mt-0.5">
      Limit set by AskProdigy team
    </p>
  );
};

// FE-canonical (`USAGE_METRICS[].key` / UsageSnapshot keys) → backend's
// `overridden_keys` canonical-name vocabulary per Storm's contract
// (`f12affe`). The two diverge on email (FE `_threads` vs backend
// `_replies`) and knowledge (FE `_summaries` vs backend `_sources`).
// Everything else is identity-mapped.
//
// 🚨 The map itself MOVED to `types/billing.ts` (2026-08-17) and is only
// re-exported here so existing importers keep working. It had to move because
// `checkPlanCardLimitDrift` needs the same remap, and importing a component
// module from the types module would have created a cycle — two copies of a
// two-entry map is exactly how the FE/backend key vocabularies drift apart
// again, which is the bug this map exists to prevent.
export { OVERRIDE_KEY_REMAP, overrideKeyFor } from '../../types/billing';

// ---------- usage bar ----------
export const UsageBar: React.FC<{
  label: string;
  used: number;
  limit: number;
  unit: string;
  /** Optional override for how used/limit are rendered in the right-hand
   *  readout. The pooled Text bar passes `formatTokenCount` so 12,400,000
   *  reads `12.4M` instead of a 10-character number that wraps at 375px.
   *  Omitted → today's `toLocaleString()` + `min` suffix, unchanged. */
  formatValue?: (n: number) => string;
  /** FAULT state — the limit for this resource is missing or unusable, so
   *  we do not know the allowance. Renders neutral: empty track, no colour,
   *  no percentage, and "Usage data unavailable" in place of the readout.
   *
   *  🚨 This is deliberately NOT the exhausted state. Falling back to `0`
   *  would paint a full red bar, which tells the customer "you are out" —
   *  a false claim, made in order to signal a broken contract to US. They
   *  would believe it and open a support ticket. Neutral-and-obviously-odd
   *  is just as reportable and says nothing untrue. */
  unknown?: boolean;
  /** Overrides the fill colour that `barColor(pct)` would pick. The pooled
   *  Text bar uses it to go red at 95% instead of 100%, matching the
   *  backend's critical alert. Omitted → the shared 80/100 ladder, so no
   *  other bar changes. */
  colorClass?: string;
  /** Overrides the fill percentage. 🚨 Exists so the two pooled Text meters
   *  can render a percentage that `deriveTextPool` computed ONCE, rather than
   *  each bar re-deriving it from its own numerator/denominator. That matters
   *  because the plan bar's displayed numerator is CLAMPED to the plan cap
   *  (see `planUsed`), so re-deriving inside the bar would report 0% for the
   *  hard-cap-at-zero tier and would silently drift from the tier/colour the
   *  parent already chose. Omitted → `usagePercent(used, limit)`, unchanged
   *  for every other bar. */
  pctOverride?: number;
}> = ({ label, used, limit, unit, formatValue, unknown = false, colorClass, pctOverride }) => {
  const pct = pctOverride ?? usagePercent(used, limit);
  const unlimited = limit < 0;
  const fmt =
    formatValue ??
    ((n: number) => `${n.toLocaleString()}${unit === 'min' ? ' min' : ''}`);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs sm:text-sm gap-2">
        <span className="font-medium text-gray-700 dark:text-gray-300 truncate">{label}</span>
        {unknown ? (
          <span className="text-gray-500 dark:text-gray-400 italic text-[11px] sm:text-xs flex-shrink-0">
            Usage data unavailable
          </span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400 font-mono text-[11px] sm:text-xs flex-shrink-0">
            {fmt(used)} / {unlimited ? '∞' : fmt(limit)}
          </span>
        )}
      </div>
      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        {!unknown && (
          <div
            className={`h-full ${
              unlimited ? 'bg-emerald-500' : colorClass ?? barColor(pct)
            } transition-all`}
            style={{ width: unlimited ? (used > 0 ? '10%' : '0%') : `${Math.max(pct, used > 0 ? 3 : 0)}%` }}
          />
        )}
      </div>
    </div>
  );
};

// ---------- pooled Text+Voice billing (Plan-TextVoicePoolBilling-2026-08) ----------

// Compact AI-token readout: 12,400,000 → `12.4M`. Pools run to eight and
// nine figures, so the raw `toLocaleString()` used by every other bar
// overflows the 375px readout. One decimal, trailing `.0` trimmed, so a
// whole-number plan limit reads `60M` and not `60.0M`.
export const formatTokenCount = (n: number): string => {
  if (n < 0) return '∞';
  const abs = Math.abs(n);
  const scale = (div: number, suffix: string) => {
    const v = n / div;
    // 1 decimal below 100 (12.4M), none above (124M) — keeps the readout
    // to at most 5 characters at every magnitude.
    const s = Math.abs(v) < 100 ? v.toFixed(1) : v.toFixed(0);
    return `${s.replace(/\.0$/, '')}${suffix}`;
  };
  if (abs >= 1_000_000_000) return scale(1_000_000_000, 'B');
  if (abs >= 1_000_000) return scale(1_000_000, 'M');
  if (abs >= 1_000) return scale(1_000, 'K');
  return n.toLocaleString();
};

// Per-surface counts shown UNDER the pooled Text bar. Informational only —
// none of these has a cap of its own in pooled mode; every one of them
// draws from the single `max_text_tokens` allowance. The counters keep
// incrementing on the backend either way (plan §A3), so these are the same
// numbers the legacy per-category bars showed, minus the limits.
//
// `alwaysShow: false` rows are hidden at 0 to avoid a permanently-dead row
// for workspaces that cannot use the feature — the same reasoning that
// gates the legacy Stylist bar on `> 0`.
const TEXT_POOL_SURFACES: Array<{
  key: keyof UsageSnapshot;
  label: string;
  alwaysShow: boolean;
}> = [
  { key: 'chat_conversations', label: 'Chat conversations', alwaysShow: true },
  { key: 'email_threads', label: 'Email replies', alwaysShow: true },
  { key: 'knowledge_summaries', label: 'Knowledge summaries', alwaysShow: true },
  { key: 'web_scrapes', label: 'Web scrapes', alwaysShow: true },
  { key: 'reports', label: 'Reports', alwaysShow: true },
  { key: 'video_analyses', label: 'Video analyses', alwaysShow: false },
  { key: 'stylist_results', label: 'Stylist results', alwaysShow: false },
  { key: 'orchestrator_messages', label: 'Orchestrator messages', alwaysShow: true },
];

// ---------- pool threshold states ----------
// Mirrors the backend alert ladder (80 / 95 / 100 → `notify_workspace_roles`
// + owner email). The bar must not still read green beside an email saying
// "you are at 80%", so both sides read the SAME constants.
type PoolTier = 'ok' | 'warn' | 'critical' | 'exhausted';

const poolTier = (pct: number): PoolTier => {
  if (pct >= TEXT_POOL_EXHAUSTED_PCT) return 'exhausted';
  if (pct >= TEXT_POOL_CRITICAL_PCT) return 'critical';
  if (pct >= TEXT_POOL_WARN_PCT) return 'warn';
  return 'ok';
};

// Fill colour per tier. Diverges from the shared `barColor` ladder on ONE
// boundary: the pool turns red at 95%, where a generic bar is still amber.
const POOL_BAR_COLOR: Record<PoolTier, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  critical: 'bg-red-500',
  exhausted: 'bg-red-500',
};

// Alert-strip styling. Opaque token backgrounds, not `glass`/`.card` — this
// strip sits inside a card and must read cleanly in both themes.
const POOL_ALERT_STYLE: Record<'warn' | 'critical' | 'exhausted', string> = {
  warn: 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800',
  critical: 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800',
  exhausted:
    'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800',
};

// ---------- THE derivation ----------
//
// 🚨 EVERY number rendered by the two Text meters comes out of THIS function
// and nowhere else. The bars do not recompute anything — not a percentage, not
// a tier, not a remainder. That is the whole guarantee: two widgets fed by one
// arithmetic partition cannot disagree, because there is only one arithmetic.
//
// THE PARTITION (2026-08-17, owner: "isolate the topped up tokens in the
// display"). Two genuinely separate pools, spent in a strict order, verified in
// `back/utils/text_pool.py::debit_text_tokens`:
//
//     prev     = new_total - amount
//     overflow = max(0, new_total - max(limit, prev))
//
// Only the slice of a debit landing ABOVE `max_text_tokens` touches a top-up.
// Summed over a cycle that telescopes to exactly `max(0, used - limit)`, so:
//
//     planUsed  = min(used, limit)          ← paid for by the monthly plan
//     topUpUsed = max(0, used - limit)      ← paid for by purchased tokens
//     planUsed + topUpUsed === used         ← EXACT, no token counted twice
//
// 🚨 THE BUG THIS REPLACES: the plan bar's denominator used to be
// `max_text_tokens + text_tokens_topup`, so the plan bar ALREADY absorbed the
// top-up. Adding a second top-up meter on top of that would have rendered the
// same tokens in two widgets. The blended denominator existed to stop a
// just-paid customer seeing a bar pinned at 100%; that job now belongs to the
// SECOND METER (a full plan bar beside a top-up meter with balance left is
// self-evidently not "you are out"), and to the alert copy below, which no
// longer claims exhaustion while purchased tokens remain. Do NOT reinstate the
// blended denominator — it is now double-counting, not a fix.
//
// 🚨 THE TOP-UP DENOMINATOR IS DELIBERATELY *NOT* LIFETIME TOKENS PURCHASED.
// It is `remaining + drawn this cycle` — the top-up capacity this cycle could
// draw on, including anything bought mid-cycle. Two reasons: (1) it keeps both
// meters cycle-scoped, so the Monthly Usage card reads consistently; (2) it
// needs no second network call, so the meter works for non-owners (who are
// refused `/bundles/text-topups`) and inside ShopifyBillingView (Stripe-only
// endpoint). Lifetime purchased/paid totals are the AI Text Top-Ups section's
// job and stay there.
type PoolMeters = {
  /** false → the limit is missing/unusable; both meters go neutral. */
  limitKnown: boolean;
  unlimited: boolean;
  /** limitKnown && !unlimited — the only state in which a split is meaningful. */
  measurable: boolean;
  /** Displayed numerator of the plan bar, CLAMPED to the cap. Above-cap usage
   *  is not the plan's; it appears in `topUpUsed` instead. */
  planUsed: number;
  planLimit: number;
  /** From the UNCLAMPED `used`, so a `limit === 0` plan (the unrecognized-plan
   *  hard-cap tier) still reads 100% rather than 0/0. */
  planPct: number;
  planTier: PoolTier;
  /** Tokens drawn above the cap this cycle === the draw on purchased tokens. */
  topUpUsed: number;
  /** REMAINING durable balance. 🚨 `null` means NOT APPLICABLE (the key is
   *  omitted while `TEXT_POOL_BILLING_ENABLED` is off) — never conflated with
   *  a real `0`, which is a genuinely spent-out balance. */
  topUpRemaining: number | null;
  /** remaining + drawn this cycle. `null` when not applicable. */
  topUpCapacity: number | null;
  topUpPct: number | null;
  topUpTier: PoolTier | null;
  /** Gate for the whole top-up meter: there is capacity to meter. */
  showTopUpMeter: boolean;
  /** The TRUE hard stop — plan cap reached AND no purchased tokens left. Only
   *  in this state is "you've used all your AI text tokens" a true sentence. */
  hardBlocked: boolean;
};

export const deriveTextPool = (
  usage: Pick<UsageSnapshot, 'text_tokens' | 'text_tokens_topup'>,
  limit?: number,
): PoolMeters => {
  const limitKnown = typeof limit === 'number' && Number.isFinite(limit);
  const planLimit = limitKnown ? (limit as number) : 0;
  const unlimited = limitKnown && planLimit < 0;
  const measurable = limitKnown && !unlimited;
  const used = Math.max(0, usage.text_tokens ?? 0);

  // 🚨 THREE STATES, AND A `?? 0` COLLAPSES TWO OF THEM (fixed once already in
  // `95b40f0`). Absent = pooled billing is off, the field does not apply.
  // `0` = a real, fully-spent balance. `> 0` = money the customer is holding.
  const rawTopUp = usage.text_tokens_topup;
  const topUpRemaining =
    typeof rawTopUp === 'number' && Number.isFinite(rawTopUp) ? Math.max(0, rawTopUp) : null;

  const planUsed = measurable ? Math.min(used, Math.max(planLimit, 0)) : used;
  const topUpUsed = measurable ? Math.max(0, used - planLimit) : 0;
  const planPct = measurable ? usagePercent(used, planLimit) : 0;
  const planTier: PoolTier = measurable ? poolTier(planPct) : 'ok';

  const topUpCapacity =
    measurable && topUpRemaining !== null ? topUpRemaining + topUpUsed : null;
  const topUpPct =
    topUpCapacity !== null && topUpCapacity > 0
      ? usagePercent(topUpUsed, topUpCapacity)
      : null;

  return {
    limitKnown,
    unlimited,
    measurable,
    planUsed,
    planLimit,
    planPct,
    planTier,
    topUpUsed,
    topUpRemaining,
    topUpCapacity,
    topUpPct,
    topUpTier: topUpPct === null ? null : poolTier(topUpPct),
    showTopUpMeter: topUpCapacity !== null && topUpCapacity > 0,
    hardBlocked: measurable && planPct >= TEXT_POOL_EXHAUSTED_PCT && (topUpRemaining ?? 0) <= 0,
  };
};

/** The pooled Text bar + its per-surface breakdown. Shared by BOTH billing
 *  views (BillingPage.tsx and ShopifyBillingView.tsx) so the pooled shape
 *  can't drift the way the two legacy Monthly Usage sections did. */
export const TextPoolBar: React.FC<{
  usage: UsageSnapshot;
  /** RAW `usage_limits.max_text_tokens` — pass it through undefined, do NOT
   *  `?? 0` at the call site. A genuine `0` is a real allowance ("hard-cap
   *  at zero", the unrecognized-plan tier) and correctly renders exhausted;
   *  an ABSENT limit means we were told pooled pricing is on but never told
   *  the size of the pool. Collapsing the two makes a contract fault
   *  indistinguishable from a customer being out of tokens. */
  limit?: number;
  overriddenKeys?: string[];
  /** Opens the Text Top-Up purchase modal. 🚨 STRIPE-ONLY — pass it from
   *  BillingPage and NEVER from ShopifyBillingView, which shares this
   *  component but buys through Shopify Managed Pricing. Omitted → the
   *  exhausted state still renders, just without a buy CTA. Callers should
   *  also omit it for non-owners, who cannot purchase. */
  onTopUp?: () => void;
}> = ({ usage, limit, overriddenKeys, onTopUp }) => {
  const rows = TEXT_POOL_SURFACES.filter((s) => {
    const v = usage[s.key];
    return s.alwaysShow || (typeof v === 'number' && v > 0);
  });

  // 🚨 ONE call, ONE partition — every figure both meters render comes from
  // here. See `deriveTextPool` for why the plan bar no longer blends the
  // top-up into its denominator.
  const d = deriveTextPool(usage, limit);
  const tier = d.planTier;
  const pct = d.planPct;
  const hasTopUpLeft = (d.topUpRemaining ?? 0) > 0;

  // ---------- CONTRACT TRIPWIRES (dev-only, compiled out of prod) ----------
  //
  // 🚨 THE OLD TRIPWIRE IS GONE ON PURPOSE, AND THIS IS WHAT REPLACED IT.
  // It asserted "a positive `text_tokens_topup` and a 100% pool can never both
  // be true". Under the split meters that pair is not merely reachable, it is
  // the state the owner asked to be able to WATCH: the plan bar legitimately
  // reads 100% while purchased tokens remain. Left in place it would have
  // fired on every correct handover.
  //
  // The failure it existed to catch has not gone away: `text_tokens_topup`
  // silently reverting from REMAINING to GRANTED-this-cycle. Both readings have
  // the same name and type and neither raises. What changed is that the revert
  // is no longer visible in a SINGLE render — the balance is durable now, so
  // there is no relation between a lifetime remainder and a cycle's overflow
  // that holds under one reading and breaks under the other. (Checked:
  // `overflow >= remaining` is a perfectly legal state under REMAINING, so the
  // obvious one-shot check would have fired on healthy data.)
  //
  // So the instrument became TEMPORAL. `debit_text_tokens` gives one law:
  //
  //     tokens leave the balance IF AND ONLY IF they are drawn above the cap
  //
  // so between any two comparable renders, the DROP in `text_tokens_topup` must
  // EQUAL the RISE in above-cap usage. Both readings break that law, in opposite
  // directions, and each gets its own clause below:
  //
  //   drop > rise  → tokens vanished with no draw to account for them. Fires on
  //                  `text_tokens` being clamped to the cap server-side (which
  //                  would freeze the top-up meter at 0% while the balance
  //                  quietly drained), and on the balance being reset per cycle
  //                  instead of carried — i.e. the no-expiry decision reverting.
  //   rise > 0 and
  //   drop <= 0    → THE GRANTED SIGNATURE. Under GRANTED the figure is constant
  //                  for the cycle, so it does not move while tokens are drawn
  //                  above the cap. Note this is the clause the "drop-only"
  //                  version of this check would have MISSED entirely — a flat
  //                  balance never drops, so half the instrument would have been
  //                  decorative.
  //
  // The one ambiguity is a real purchase, which also raises the balance against
  // the drain. It is excluded by the `!purchased` guard: a purchase is a jump
  // ABOVE the previous balance, whereas GRANTED holds it exactly level.
  const prev = React.useRef<{ used: number; limit: number; remaining: number } | null>(null);
  if (import.meta.env.DEV) {
    const snap =
      d.measurable && d.topUpRemaining !== null
        ? { used: usage.text_tokens ?? 0, limit: d.planLimit, remaining: d.topUpRemaining }
        : null;
    const before = prev.current;
    if (
      snap &&
      before &&
      before.limit === snap.limit && // plan change → different cap, not comparable
      snap.used >= before.used // cycle reset (the counter drops) → not comparable
    ) {
      const drop = before.remaining - snap.remaining;
      // Above-cap draw, not raw usage: usage below the cap is the plan's and
      // must not be expected to move the balance at all.
      const rise = Math.max(0, snap.used - d.planLimit) - Math.max(0, before.used - d.planLimit);
      const purchased = snap.remaining > before.remaining;
      const fault =
        drop > rise ? 'drained' : rise > 0 && drop <= 0 && !purchased ? 'frozen' : null;
      if (fault) {
        // eslint-disable-next-line no-console
        console.error(
          '[TextPoolBar] BROKEN INVARIANT (%s): top-up balance moved by %d while above-plan ' +
            'usage moved by %d. Purchased tokens leave the balance if and only if they are ' +
            'drawn above the plan cap, so these two must move together. Either ' +
            '`text_tokens_topup` has reverted from REMAINING to GRANTED-this-cycle, or ' +
            '`text_tokens` is being clamped to `max_text_tokens`, or top-ups have started ' +
            'expiring again. Treat as a contract change, not a rename.',
          fault,
          -drop,
          rise,
        );
      }
    }
    if (snap) prev.current = snap;
  }

  // Second, synchronous fault: usage has gone above the plan cap on a workspace
  // that has no top-up account at all. `check_text_pool` blocks at the cap
  // unless purchased tokens cover the overflow, so either the block is broken
  // or `text_tokens_topup` was stripped from a payload that is still pooled.
  // Either way the top-up meter would silently vanish and the overflow would be
  // rendered by nothing — the split's one blind spot, so it gets an instrument.
  if (import.meta.env.DEV && d.measurable && d.topUpUsed > 0 && d.topUpRemaining === null) {
    // eslint-disable-next-line no-console
    console.error(
      '[TextPoolBar] UNREACHABLE STATE: %d tokens used above the plan cap while ' +
        '`text_tokens_topup` is ABSENT. Above-cap usage requires purchased tokens; an ' +
        'absent key means pooled billing is off, which contradicts this bar rendering.',
      d.topUpUsed,
    );
  }

  return (
    <div>
      {/* ── Meter 1 of 2: the MONTHLY PLAN allowance, and nothing else. ──
          🚨 The numerator is CLAMPED to the cap (`planUsed`). Above-cap tokens
          are not the plan's — they belong to the meter below, and rendering
          them here as well is the double-count this split exists to remove. */}
      <UsageBar
        label="Plan AI Text Tokens"
        used={d.planUsed}
        limit={d.limitKnown ? d.planLimit : 0}
        unit="tokens"
        formatValue={formatTokenCount}
        unknown={!d.limitKnown}
        colorClass={d.measurable ? POOL_BAR_COLOR[tier] : undefined}
        pctOverride={d.measurable ? pct : undefined}
      />
      <OverrideDisclosure resourceKey="text_tokens" overriddenKeys={overriddenKeys} />

      {/* ── Meter 2 of 2: PURCHASED tokens, on their own scale. ──
          Owner 2026-08-17: "isolate the topped up tokens in the display… the
          meter in the Top up credits". A durable pool that is only ever touched
          by above-plan usage is a different thing from a perishable monthly
          allowance, and blending them hid the handover the owner wants to see.

          🚨 RENDER RULE — `showTopUpMeter` is "there is capacity to meter this
          cycle", NOT "has ever purchased". The two differ in exactly one state:
          a top-up bought and fully spent in an EARLIER cycle, which meters
          0 / 0 and is a dead widget. That receipt is not lost — it is listed
          with a "Spent" badge in AI Text Top-Ups, which is the surface that
          owns purchase history. A top-up spent out in the CURRENT cycle still
          meters here, at 100%, because its capacity is part of this cycle.

          The alternative render rule (fetch `/bundles/text-topups` for a
          lifetime "ever purchased" flag) was rejected: that endpoint is
          owner+admin only and Stripe-only, so the meter would disappear for
          members and for the whole Shopify billing view — a meter that depends
          on who is looking at it. */}
      {d.showTopUpMeter && (
        <div className="mt-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2.5">
          <UsageBar
            label="Top-Up Tokens"
            used={d.topUpUsed}
            limit={d.topUpCapacity as number}
            unit="tokens"
            formatValue={formatTokenCount}
            colorClass={POOL_BAR_COLOR[d.topUpTier as PoolTier]}
            pctOverride={d.topUpPct as number}
          />
          <p className="text-[11px] text-emerald-800 dark:text-emerald-300 mt-1.5 flex items-start gap-1.5">
            <Wallet className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span>
              {hasTopUpLeft ? (
                <>
                  <span className="font-semibold tabular-nums">
                    {formatTokenCount(d.topUpRemaining as number)}
                  </span>{' '}
                  remaining — top-ups never expire, and are only drawn on once
                  your plan allowance runs out.
                </>
              ) : (
                <>
                  Top-up balance used up. Your plan allowance still resets at the
                  start of your next billing period.
                </>
              )}
            </span>
          </p>
        </div>
      )}

      {/* Disclosure: WHY the number moves the way it does. Plain language on
          purpose — the pool unit is weighted (uncached input ×1, cached input
          ×0.1, output ×6) and dumping that formula on a billing page reads as
          an excuse rather than an explanation. */}
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          Writing replies uses the most tokens, reading your content uses far
          less, and anything your assistant re-reads costs the least.
        </span>
      </p>

      {/* Threshold states — 80% / 95% / 100%, the same boundaries that fire
          the in-app notification and owner email.
          🚨 These now describe the PLAN allowance, which is all the bar above
          measures. Every sentence here is branched on whether purchased tokens
          remain, because the un-branched versions become FALSE the moment the
          plan bar can legitimately read 100% with a top-up still covering the
          workspace. "You've used all your AI text tokens" and "AI text features
          are paused" would both be flat untrue in exactly the state the owner
          asked to be able to see. `hardBlocked` — plan cap reached AND nothing
          purchased left — is the only state in which they are true, and it is
          the same condition the backend blocks on. */}
      {tier !== 'ok' && (
        <div
          className={`mt-2.5 rounded-lg border px-3 py-2 ${
            // Plan spent but covered by a top-up is NOT an alarm — nothing is
            // broken and nothing is paused, so it drops off the red ladder to
            // amber and swaps the warning triangle for a wallet.
            tier === 'exhausted' && !d.hardBlocked ? POOL_ALERT_STYLE.warn : POOL_ALERT_STYLE[tier]
          }`}
          role="status"
        >
          <div className="flex items-start gap-2">
            {tier === 'exhausted' && !d.hardBlocked ? (
              <Wallet className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs font-semibold leading-snug">
                {tier === 'exhausted'
                  ? d.hardBlocked
                    ? "You've used all your AI text tokens"
                    : 'Monthly plan allowance used up'
                  : `${pct}% of your monthly plan allowance used`}
              </p>
              <p className="text-[11px] leading-snug mt-0.5 opacity-90">
                {tier === 'exhausted'
                  ? d.hardBlocked
                    ? 'AI text features are paused until your allowance resets or you top up.'
                    : `Your top-up balance is covering AI text usage now — ${formatTokenCount(
                        d.topUpRemaining as number,
                      )} left.`
                  : tier === 'critical'
                    ? hasTopUpLeft
                      ? 'Your top-up balance takes over once this runs out.'
                      : 'Almost out — top up now to avoid an interruption.'
                    : 'Your allowance resets at the start of your next billing period.'}
              </p>
              {/* Full-width at 375px; auto-width from `sm` up. */}
              {onTopUp && tier !== 'warn' && (
                <button
                  onClick={onTopUp}
                  className="mt-2 w-full sm:w-auto px-3 py-1.5 rounded-lg bg-gradient-to-r from-primary to-purple-600 text-white text-xs font-semibold hover:shadow-lg transition-all"
                >
                  Buy Text Top-Up
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* One column at 375px: at `text-[11px]` in a half-width mobile cell,
          "Knowledge summaries" truncates against its own count. Two columns
          only from `sm` up, where the card is wide enough for both. */}
      <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
        {rows.map((s) => (
          <div key={s.key} className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
              {s.label}
            </span>
            <span className="text-[11px] font-mono text-gray-600 dark:text-gray-300 tabular-nums flex-shrink-0">
              {((usage[s.key] as number) ?? 0).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
      <p className="text-[11px] italic text-gray-500 dark:text-gray-400 mt-2">
        Every AI text surface draws from the same token allowance — your plan
        first, then any top-up. These counts are for reference, not separate
        limits.
      </p>
    </div>
  );
};
